import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import apiService from '../services/api';
import { PLAN_DETAILS } from '../config/stripe';

interface Gym {
  id: number;
  name: string;
  address: string;
  phone: string;
  email: string;
  is_active: boolean;
  member_count: number;
  monthly_revenue?: number;
  created_at: string;
  last_activity: string;
  owner_name: string;
  owner_email: string;
  subscription_status?: string;
  subscription_price_id?: string;
  connect_account_status?: string;
}

const CEOEnrolledGymsPage: React.FC = () => {
  const { theme } = useTheme();
  const [gyms, setGyms] = useState<Gym[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGym, setSelectedGym] = useState<Gym | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [showSuccess, setShowSuccess] = useState<string | null>(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [selectedGymForSubscription, setSelectedGymForSubscription] = useState<Gym | null>(null);
  const [showDecommissionModal, setShowDecommissionModal] = useState(false);
  const [selectedGymForDecommission, setSelectedGymForDecommission] = useState<Gym | null>(null);

  useEffect(() => {
    fetchGyms();
  }, []);

  const fetchGyms = async () => {
    try {
      setLoading(true);
      const gymsData = await apiService.getCEOGyms();
      setGyms(gymsData);
    } catch (error) {
      console.error('Error fetching gyms:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredGyms = gyms.filter(gym => {
    const matchesSearch = gym.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         gym.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         gym.owner_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || 
                         (filterStatus === 'active' && gym.is_active) ||
                         (filterStatus === 'inactive' && !gym.is_active);
    
    return matchesSearch && matchesFilter;
  });

  const handleViewDetails = async (gym: Gym) => {
    try {
      // Fetch full gym details including subscription info
      const gymDetails = await apiService.getCEOGymDetails(gym.id);
      setSelectedGym({ ...gym, ...gymDetails });
      setShowDetails(true);
    } catch (error) {
      console.error('Failed to fetch gym details:', error);
      setSelectedGym(gym);
      setShowDetails(true);
    }
  };

  const handleAccountTakeover = async (gym: Gym) => {
    try {
      const result = await apiService.takeoverGymAccount(gym.id);
      // Open takeover URL in new tab
      window.open(result.loginUrl, '_blank');
      setShowSuccess(`Account takeover initiated for ${gym.name}`);
      setTimeout(() => setShowSuccess(null), 5000);
    } catch (error: any) {
      console.error('Failed to initiate account takeover:', error);
      alert(error.message || 'Failed to initiate account takeover');
    }
  };

  const handleManageSubscription = (gym: Gym) => {
    setSelectedGymForSubscription(gym);
    setShowSubscriptionModal(true);
  };

  const handleDecommission = (gym: Gym) => {
    setSelectedGymForDecommission(gym);
    setShowDecommissionModal(true);
  };

  const handleUpdateSubscription = async (priceId: string) => {
    if (!selectedGymForSubscription) return;
    
    try {
      await apiService.updateGymSubscription(selectedGymForSubscription.id, priceId);
      setShowSuccess(`Subscription updated for ${selectedGymForSubscription.name}`);
      setShowSubscriptionModal(false);
      setSelectedGymForSubscription(null);
      await fetchGyms();
      setTimeout(() => setShowSuccess(null), 5000);
    } catch (error: any) {
      alert(error.message || 'Failed to update subscription');
    }
  };

  const handleCancelSubscription = async () => {
    if (!selectedGymForSubscription) return;
    
    if (!window.confirm(`Are you sure you want to cancel the subscription for ${selectedGymForSubscription.name}?`)) {
      return;
    }

    try {
      await apiService.cancelGymSubscription(selectedGymForSubscription.id);
      setShowSuccess(`Subscription canceled for ${selectedGymForSubscription.name}`);
      setShowSubscriptionModal(false);
      setSelectedGymForSubscription(null);
      await fetchGyms();
      setTimeout(() => setShowSuccess(null), 5000);
    } catch (error: any) {
      alert(error.message || 'Failed to cancel subscription');
    }
  };

  const handleConfirmDecommission = async (reason?: string, notes?: string) => {
    if (!selectedGymForDecommission) return;

    if (!window.confirm(`Are you sure you want to decommission ${selectedGymForDecommission.name}? This will deactivate the gym and all associated users.`)) {
      return;
    }

    try {
      await apiService.decommissionGym(selectedGymForDecommission.id, reason, notes);
      setShowSuccess(`${selectedGymForDecommission.name} has been decommissioned`);
      setShowDecommissionModal(false);
      setSelectedGymForDecommission(null);
      await fetchGyms();
      setTimeout(() => setShowSuccess(null), 5000);
    } catch (error: any) {
      alert(error.message || 'Failed to decommission gym');
    }
  };


  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadge = (isActive: boolean, currentTheme: 'light' | 'dark' = theme) => {
    const textColor = currentTheme === 'dark' ? '400' : '700';
    const borderColor = currentTheme === 'dark' ? '400' : '500';
    
    return isActive ? (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-${textColor} border border-green-${borderColor}/30`}>
        Active
      </span>
    ) : (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-${textColor} border border-red-${borderColor}/30`}>
        Inactive
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-white">Loading gyms...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-white">Enrolled Gyms</h1>
            <p className="mt-2 text-gray-300">
              Manage and monitor all enrolled gyms in your network.
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-300">
              {filteredGyms.length} of {gyms.length} gyms
            </span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Search Gyms
            </label>
            <input
              type="text"
              placeholder="Search by name, address, or owner..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-white placeholder-gray-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Filter by Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as 'all' | 'active' | 'inactive')}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-white"
            >
              <option value="all" className="bg-gray-800">All Gyms</option>
              <option value="active" className="bg-gray-800">Active Only</option>
              <option value="inactive" className="bg-gray-800">Inactive Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Gyms Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredGyms.map((gym) => (
          <div key={gym.id} className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 hover:border-white/30 transition-all duration-200">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-white">{gym.name}</h3>
                  <p className="text-sm text-gray-300">{gym.address}</p>
                </div>
                {getStatusBadge(gym.is_active, theme)}
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-300">Members:</span>
                  <span className="font-medium text-white">{gym.member_count}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-300">Monthly Revenue:</span>
                  <span className="font-medium text-white">
                    {gym.monthly_revenue !== undefined ? formatCurrency(gym.monthly_revenue) : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-300">Owner:</span>
                  <span className="font-medium text-white">{gym.owner_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-300">Enrolled:</span>
                  <span className="font-medium text-white">{formatDate(gym.created_at)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-300">Subscription:</span>
                  <span className={`font-medium ${
                    gym.subscription_status === 'active' ? 'text-green-400' : 
                    gym.subscription_status === 'canceled' ? 'text-red-400' : 
                    'text-yellow-400'
                  }`}>
                    {gym.subscription_status || 'None'}
                  </span>
                </div>
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => handleViewDetails(gym)}
                  className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-4 py-2 rounded-lg hover:from-blue-600 hover:to-cyan-600 transition-all duration-200 text-sm font-medium"
                >
                  View Details
                </button>
                <button
                  onClick={() => handleAccountTakeover(gym)}
                  className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all duration-200 text-sm font-medium"
                  title="Login as gym owner to help debug"
                >
                  Account Takeover
                </button>
                <button
                  onClick={() => handleManageSubscription(gym)}
                  className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all duration-200 text-sm font-medium"
                  title="Manage subscription"
                >
                  Subscription
                </button>
                <button
                  onClick={() => handleDecommission(gym)}
                  className="flex-1 bg-gradient-to-r from-red-500 to-orange-500 text-white px-4 py-2 rounded-lg hover:from-red-600 hover:to-orange-600 transition-all duration-200 text-sm font-medium"
                  title="Decommission gym"
                >
                  Decommission
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredGyms.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">🏋️‍♂️</div>
          <h3 className="text-lg font-medium text-white mb-2">No gyms found</h3>
          <p className="text-gray-300">
            {searchTerm ? 'Try adjusting your search terms.' : 'No gyms match your current filters.'}
          </p>
        </div>
      )}

      {/* Gym Details Modal */}
      {showDetails && selectedGym && (
        <div 
          className="fixed inset-0 lg:left-64 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowDetails(false);
            }
          }}
        >
          <div 
            className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{selectedGym.name}</h2>
                  <p className="text-gray-500">{selectedGym.address}</p>
                </div>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Gym Information</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Phone</label>
                      <p className="text-gray-900">{selectedGym.phone}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Email</label>
                      <p className="text-gray-900">{selectedGym.email}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Status</label>
                      <div className="mt-1">{getStatusBadge(selectedGym.is_active, theme)}</div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Enrolled Date</label>
                      <p className="text-gray-900">{formatDate(selectedGym.created_at)}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Owner Information</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Owner Name</label>
                      <p className="text-gray-900">{selectedGym.owner_name}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Owner Email</label>
                      <p className="text-gray-900">{selectedGym.owner_email}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Last Activity</label>
                      <p className="text-gray-900">{formatDate(selectedGym.last_activity)}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Statistics</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500">Total Members</p>
                    <p className="text-2xl font-bold text-gray-900">{selectedGym.member_count}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500">Subscription Status</p>
                    <p className="text-lg font-bold text-gray-900">
                      {selectedGym.subscription_status || 'None'}
                    </p>
                    {selectedGym.subscription_price_id && (
                      <p className="text-xs text-gray-600">
                        {PLAN_DETAILS[selectedGym.subscription_price_id as keyof typeof PLAN_DETAILS]?.name || 'Unknown Plan'}
                      </p>
                    )}
                  </div>
                  {selectedGym.monthly_revenue !== undefined && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500">Monthly Revenue</p>
                      <p className="text-2xl font-bold text-gray-900">{formatCurrency(selectedGym.monthly_revenue)}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-8 flex justify-end space-x-4">
                <button
                  onClick={() => setShowDetails(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
                <button
                  onClick={() => handleAccountTakeover(selectedGym)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Account Takeover
                </button>
                <button
                  onClick={() => {
                    setShowDetails(false);
                    handleManageSubscription(selectedGym);
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Manage Subscription
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Management Modal */}
      {showSubscriptionModal && selectedGymForSubscription && (
        <div className="fixed inset-0 lg:left-64 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Manage Subscription - {selectedGymForSubscription.name}
            </h2>
            
            {selectedGymForSubscription.subscription_status && selectedGymForSubscription.subscription_status !== 'none' ? (
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500">Current Plan</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {PLAN_DETAILS[selectedGymForSubscription.subscription_price_id as keyof typeof PLAN_DETAILS]?.name || 'Unknown'}
                  </p>
                  <p className="text-sm text-gray-600">
                    Status: <span className="font-medium">{selectedGymForSubscription.subscription_status}</span>
                  </p>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700">Change Plan:</p>
                  {Object.entries(PLAN_DETAILS).map(([priceId, plan]) => (
                    <button
                      key={priceId}
                      onClick={() => handleUpdateSubscription(priceId)}
                      disabled={selectedGymForSubscription.subscription_price_id === priceId}
                      className={`w-full text-left p-3 rounded-lg border ${
                        selectedGymForSubscription.subscription_price_id === priceId
                          ? 'bg-blue-50 border-blue-500'
                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                      } ${selectedGymForSubscription.subscription_price_id === priceId ? 'cursor-not-allowed opacity-50' : ''}`}
                    >
                      <div className="font-medium">{plan.name}</div>
                      <div className="text-sm text-gray-600">${plan.price}/{plan.interval}</div>
                    </button>
                  ))}
                </div>

                <button
                  onClick={handleCancelSubscription}
                  className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Cancel Subscription
                </button>
              </div>
            ) : (
              <div>
                <p className="text-gray-600 mb-4">No active subscription. Select a plan:</p>
                <div className="space-y-2">
                  {Object.entries(PLAN_DETAILS).map(([priceId, plan]) => (
                    <button
                      key={priceId}
                      onClick={() => handleUpdateSubscription(priceId)}
                      className="w-full text-left p-3 rounded-lg border bg-gray-50 border-gray-200 hover:bg-gray-100"
                    >
                      <div className="font-medium">{plan.name}</div>
                      <div className="text-sm text-gray-600">${plan.price}/{plan.interval}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setShowSubscriptionModal(false);
                  setSelectedGymForSubscription(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Decommission Modal */}
      {showDecommissionModal && selectedGymForDecommission && (
        <div className="fixed inset-0 lg:left-64 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Decommission Gym - {selectedGymForDecommission.name}
            </h2>
            <p className="text-gray-600 mb-4">
              This will deactivate the gym and all associated users. The subscription will be canceled. This action cannot be undone.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason (optional)
                </label>
                <input
                  type="text"
                  id="decommissionReason"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="e.g., Contract ended, requested cancellation"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (optional)
                </label>
                <textarea
                  id="decommissionNotes"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Additional notes..."
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-4">
              <button
                onClick={() => {
                  setShowDecommissionModal(false);
                  setSelectedGymForDecommission(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const reason = (document.getElementById('decommissionReason') as HTMLInputElement)?.value;
                  const notes = (document.getElementById('decommissionNotes') as HTMLTextAreaElement)?.value;
                  handleConfirmDecommission(reason, notes);
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Decommission Gym
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {showSuccess && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50">
          <div className="flex items-center justify-between">
            <span>{showSuccess}</span>
            <button
              onClick={() => setShowSuccess(null)}
              className="ml-4 text-white hover:text-gray-200"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CEOEnrolledGymsPage;
